import { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  Badge,
  Banner,
  Button,
  Caption,
  List,
  Placeholder,
  Section,
} from '@telegram-apps/telegram-ui';
import {
  AnswerView,
  AuthorActionResponse,
  BoxOwnerView,
  QuestionInboxItem,
  type BoxFormat,
  type ReferralChannel,
  ReportReason,
} from '@veylme/shared';

import { apiFetch, getInbox } from '../api/client.js';
import { humanizeApiError } from '../lib/apiError.js';
import { InboxBubbleSkeleton, InboxSkeleton } from '../components/Skeleton.js';
import {
  dismissCodeOnboarding,
  isCodeOnboardingDismissed,
} from '../lib/codeOnboarding.js';
import { requestWriteAccessSafe } from '../lib/tg.js';
import { useT } from '../hooks/useT.js';
import { useBackButton } from '../lib/nativeUi.js';
import { InvitePanel } from './InvitePanel.js';
import { QuestionCard } from './QuestionCard.js';
import styles from './InboxScreen.module.css';

const PAGE_LIMIT = 20;
const WRITE_ACCESS_FLAG = 'veyl_write_access_asked';

/**
 * Категории входящих (Веха 13.x «аккордеоны»). Каждая категория — свой
 * Accordion, идущий друг за другом; пустые (0 вопросов) не рисуются:
 *  - unanswered → «Неотвеченные», status='pending'; раскрыт по умолчанию,
 *    бейдж новых (primary).
 *  - answered   → «Отвеченные», status='answered'; серое число.
 *  - hidden     → «Скрытые», status='hidden'; серое число.
 *  - blocked    → «Заблокированные», moderationFlag='blocked' | status='removed';
 *    серое число.
 * Предикаты взаимоисключающие (blocked/removed «вынимаются» из остальных).
 */
type InboxCategory = 'unanswered' | 'answered' | 'hidden' | 'blocked';

type BadgeMode = 'primary' | 'critical' | 'secondary' | 'gray' | 'white';

const isBlockedItem = (i: QuestionInboxItem): boolean =>
  i.moderationFlag === 'blocked' || i.status === 'removed';

const INBOX_CATEGORIES: ReadonlyArray<{
  key: InboxCategory;
  labelKey:
    | 'inbox_acc_unanswered'
    | 'inbox_acc_answered'
    | 'inbox_acc_hidden'
    | 'inbox_acc_blocked';
  /**
   * Что попадает в категорию (взаимоисключающие предикаты). `justAnswered` —
   * id вопросов, отвеченных только что в этой сессии: они остаются под
   * «Неотвеченными» (пузырь ответа виден на месте), пока экран не перезагрузят.
   */
  match: (item: QuestionInboxItem, justAnswered: Set<string>) => boolean;
  /** Число в бейдже категории. У «Неотвеченных» — только реально новые (pending). */
  badgeCount: (items: QuestionInboxItem[], justAnswered: Set<string>) => number;
  /** Режим бейджа: у «Неотвеченных» — primary, у прочих — серый. */
  badgeMode: BadgeMode;
}> = [
  {
    key: 'unanswered',
    labelKey: 'inbox_acc_unanswered',
    match: (i, justAnswered) =>
      !isBlockedItem(i) &&
      i.status !== 'hidden' &&
      (i.status === 'pending' || justAnswered.has(i.id)),
    badgeCount: (items) =>
      items.filter((i) => i.status === 'pending' && !isBlockedItem(i)).length,
    badgeMode: 'primary',
  },
  {
    key: 'answered',
    labelKey: 'inbox_acc_answered',
    match: (i, justAnswered) =>
      !isBlockedItem(i) && i.status === 'answered' && !justAnswered.has(i.id),
    badgeCount: (items, justAnswered) =>
      items.filter(
        (i) =>
          !isBlockedItem(i) && i.status === 'answered' && !justAnswered.has(i.id),
      ).length,
    badgeMode: 'gray',
  },
  {
    key: 'hidden',
    labelKey: 'inbox_acc_hidden',
    match: (i) => !isBlockedItem(i) && i.status === 'hidden',
    badgeCount: (items) =>
      items.filter((i) => !isBlockedItem(i) && i.status === 'hidden').length,
    badgeMode: 'gray',
  },
  {
    key: 'blocked',
    labelKey: 'inbox_acc_blocked',
    match: (i) => isBlockedItem(i),
    badgeCount: (items) => items.filter(isBlockedItem).length,
    badgeMode: 'gray',
  },
];

/** Счётчик на вкладке: подрезаем большие числа, чтобы не распирало бейдж. */
function formatBadgeCount(n: number): string {
  return n > 99 ? '99+' : String(n);
}

/**
 * InboxScreen — экран автора: список входящих вопросов (`boxFormat='ask'`,
 * по умолчанию — корневая вкладка Tabbar) ИЛИ полученных сообщений
 * (`boxFormat='message'`, work-036 Трек D — под-поток из Настроек, свой
 * заголовок/пустой список, БЕЗ код-онбординга, с нативной BackButton).
 *
 * Поток mount:
 *  1) POST /boxes { format: boxFormat } — idempotent upsert (сервер возвращает
 *     существующий бокс, если уже есть; иначе создаёт). Забираем `box.token`
 *     и `box.deepLink`.
 *  2) GET /inbox?limit=20[&format=message] — первая страница (pending+answered,
 *     сорт desc); `format` шлём ТОЛЬКО когда boxFormat !== 'ask' (контракт:
 *     без параметра сервер отдаёт обычный ask-инбокс, как раньше).
 *
 * CANON B2: `QuestionInboxItem` НЕ содержит `senderFingerprint` (zod-strict
 * на сервере select'ит без него) — здесь только то, что разрешено показывать.
 *
 * CANON G3/Q3: если `deepLink === null` (например, в env не задан BOT_USERNAME
 * до Вехи 11 deploy) — показываем плейсхолдер, НЕ падаем.
 *
 * Pending items идут сверху (status='pending'), answered ниже — обеспечивается
 * локальной сортировкой `partition` ниже, на случай если сервер вернёт
 * перемешанный порядок (контракт сейчас «desc по createdAt», который смешивает).
 */
export interface InboxScreenProps {
  /**
   * Referral-контекст (Веха 8 M2): если автор создаёт свой бокс, придя по чужому
   * deep-link — пробрасываем `{ sourceBoxToken, channel }` в `POST /boxes { ref }`
   * для атрибуции K-фактора. undefined → обычный (не-реферальный) вход.
   */
  refContext?: { sourceBoxToken: string; channel: ReferralChannel };
  /** Открыть экран шеринга карточки ответа (петля роста) — по клику «Поделиться». */
  onShareAnswer: (answer: { id: string; cardImageUrl: string | null }) => void;
  /** Поднимает `box.storyShareUrl` в App — для widgetLink в ShareScreen (Story-фолбэк F2). */
  onBoxLoaded?: (storyShareUrl: string | null) => void;
  /**
   * work-020 (решение 5): показывать ли онбординг код-входа. true, если приложение
   * открыто БЕЗ ask-токена (App: parseStartParam → INBOX). Фактический показ ещё
   * зависит от `!isCodeOnboardingDismissed()` и локального dismiss в этой сессии.
   */
  showCodeOnboarding?: boolean;
  /** Открыть экран ввода кода (ASK_BY_CODE) — кнопка онбординг-баннера. */
  onEnterCode?: () => void;
  /**
   * work-036 (Трек D): формат ящика — по умолчанию 'ask' (текущее поведение,
   * вкладка «Вопросы» в Tabbar). `'message'` — под-поток «Полученные сообщения»
   * из Настроек: тот же экран, но свой заголовок/пустой список, БЕЗ код-онбординга
   * и с нативной кнопкой «назад» (см. `onBack`).
   */
  boxFormat?: BoxFormat;
  /**
   * BackButton — возврат в Настройки. Обязателен по смыслу при `boxFormat==='message'`
   * (под-поток без Tabbar); для ask-режима (корневая вкладка) не передаётся —
   * кнопка «назад» остаётся скрытой.
   */
  onBack?: () => void;
}

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'loaded';
      box: BoxOwnerView;
      items: QuestionInboxItem[];
      nextCursor: string | null;
    }
  | { kind: 'error'; message: string };

function partitionPendingFirst(items: QuestionInboxItem[]): QuestionInboxItem[] {
  const pending = items.filter((i) => i.status === 'pending');
  const rest = items.filter((i) => i.status !== 'pending');
  return [...pending, ...rest];
}

export function InboxScreen({
  refContext,
  onShareAnswer,
  onBoxLoaded,
  showCodeOnboarding = false,
  onEnterCode,
  boxFormat = 'ask',
  onBack,
}: InboxScreenProps) {
  const t = useT();
  // work-036: BackButton навешивается ВСЕГДА (Rules of Hooks — хук не может
  // быть условным), но видна ТОЛЬКО в message-под-потоке; в ask-режиме (корневая
  // вкладка Tabbar) хук мгновенно прячет кнопку — поведение не меняется.
  useBackButton(() => onBack?.(), boxFormat === 'message');
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);
  // Категория, чья «Ещё» сейчас реально докачивает страницу — под ней рисуем
  // скелетон-пузырь (а не во всех категориях сразу: loadingMore глобальный).
  const [loadingCat, setLoadingCat] = useState<InboxCategory | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  // Вопросы, отвеченные только что в этой сессии — остаются под «Неотвеченными»
  // (пузырь ответа виден на месте), а не «уезжают» сразу в «Отвеченные».
  const [justAnswered, setJustAnswered] = useState<Set<string>>(
    () => new Set(),
  );
  // Раскрытие аккордеонов: по умолчанию открыты только «Неотвеченные».
  const [expanded, setExpanded] = useState<Record<InboxCategory, boolean>>({
    unanswered: true,
    answered: false,
    hidden: false,
    blocked: false,
  });
  // Сколько вопросов показываем в каждой категории (порциями по PAGE_LIMIT).
  const [limits, setLimits] = useState<Record<InboxCategory, number>>({
    unanswered: PAGE_LIMIT,
    answered: PAGE_LIMIT,
    hidden: PAGE_LIMIT,
    blocked: PAGE_LIMIT,
  });
  const [writeAccessAsked, setWriteAccessAsked] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WRITE_ACCESS_FLAG) === '1';
    } catch {
      return false;
    }
  });
  // work-020 (решение 5): онбординг код-входа. Скрыт навсегда, если уже закрыт
  // (localStorage), и в этой сессии после клика по крестику (локальный state).
  const [codeOnboardingClosed, setCodeOnboardingClosed] = useState<boolean>(
    () => isCodeOnboardingDismissed(),
  );

  // onBoxLoaded дёргается из mount-эффекта (deps []) — держим актуальную ссылку
  // в ref, чтобы не вносить её в deps (иначе перезагрузка ящика на каждый ререндер).
  const onBoxLoadedRef = useRef(onBoxLoaded);
  onBoxLoadedRef.current = onBoxLoaded;

  // refContext капчим в ref — mount-эффект с deps [] не должен перезапускаться.
  const refContextRef = useRef(refContext);
  refContextRef.current = refContext;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) Idempotent upsert ящика автора. Веха 8: если пришли по чужому
        // deep-link (refContext) — прикладываем `ref` для Referral-атрибуции
        // (сервер атрибутирует только на ПЕРВОМ боксе нового юзера, fail-soft).
        const refCtx = refContextRef.current;
        const box = await apiFetch('/boxes', {
          method: 'POST',
          body: refCtx
            ? { format: boxFormat, ref: refCtx }
            : { format: boxFormat },
          parse: BoxOwnerView,
        });
        if (cancelled) return;
        // 2) Первая страница inbox'а. format — только когда НЕ 'ask' (контракт:
        // без параметра сервер отдаёт обычный ask-инбокс, как раньше).
        const inbox = await getInbox({
          limit: PAGE_LIMIT,
          format: boxFormat === 'ask' ? undefined : boxFormat,
        });
        if (cancelled) return;
        setLoadState({
          kind: 'loaded',
          box,
          items: partitionPendingFirst(inbox.items),
          nextCursor: inbox.nextCursor,
        });
        // Веха 8: поднимаем story-tagged URL (для widgetLink в ShareScreen).
        onBoxLoadedRef.current?.(box.storyShareUrl);
      } catch (err: unknown) {
        if (cancelled) return;
        setLoadState({ kind: 'error', message: humanizeApiError(err, t) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLoadMore() {
    if (loadState.kind !== 'loaded' || loadState.nextCursor === null) return;
    setLoadMoreError(null);
    setLoadingMore(true);
    try {
      const inbox = await getInbox({
        cursor: loadState.nextCursor,
        limit: PAGE_LIMIT,
        format: boxFormat === 'ask' ? undefined : boxFormat,
      });
      setLoadState({
        kind: 'loaded',
        box: loadState.box,
        items: partitionPendingFirst([...loadState.items, ...inbox.items]),
        nextCursor: inbox.nextCursor,
      });
    } catch (err: unknown) {
      setLoadMoreError(humanizeApiError(err, t));
    } finally {
      setLoadingMore(false);
    }
  }

  /**
   * «Ещё» внутри аккордеона категории. Сначала раскрываем следующие 20 уже
   * загруженных вопросов этой категории; когда локально показали всё, а на
   * сервере есть ещё страницы — подтягиваем следующую (общий пул, mixed) и
   * сдвигаем порог показа категории.
   */
  async function handleCategoryMore(cat: InboxCategory, matchedLen: number) {
    if (matchedLen > limits[cat]) {
      setLimits((p) => ({ ...p, [cat]: p[cat] + PAGE_LIMIT }));
      return;
    }
    if (loadState.kind === 'loaded' && loadState.nextCursor !== null) {
      setLoadingCat(cat);
      await handleLoadMore();
      setLoadingCat(null);
      setLimits((p) => ({ ...p, [cat]: p[cat] + PAGE_LIMIT }));
    }
  }

  /**
   * Инлайн-ответ из QuestionCard: POST /answers (CANON D1 — только questionId+text),
   * затем патчим item (status='answered' + answer) и помечаем как «отвечено сейчас»,
   * чтобы вопрос остался под «Неотвеченными» с пузырём ответа на месте.
   * На ошибке бросает Error с готовым сообщением — карточка его покажет.
   */
  async function handleSubmitAnswer(questionId: string, text: string) {
    try {
      const answer = await apiFetch('/answers', {
        method: 'POST',
        body: { questionId, text },
        parse: AnswerView,
      });
      setLoadState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          ...prev,
          items: prev.items.map((it) =>
            it.id === questionId
              ? { ...it, status: 'answered' as const, answer }
              : it,
          ),
        };
      });
      setJustAnswered((prev) => new Set(prev).add(questionId));
    } catch (err: unknown) {
      throw new Error(humanizeApiError(err, t));
    }
  }

  function dismissWriteAccessBanner() {
    try {
      localStorage.setItem(WRITE_ACCESS_FLAG, '1');
    } catch {
      // localStorage недоступен (private mode) — баннер просто не запомнит dismiss.
    }
    setWriteAccessAsked(true);
  }

  async function handleRequestWriteAccess() {
    // Результат не важен для UX — баннер убираем в любом случае (CANON G3: не падаем).
    await requestWriteAccessSafe();
    dismissWriteAccessBanner();
  }

  function dismissCodeOnboardingBanner() {
    dismissCodeOnboarding();
    setCodeOnboardingClosed(true);
  }

  // Локально патчит один item в loaded-стейте (без перезагрузки inbox'а).
  function patchItem(
    questionId: string,
    patch: Partial<QuestionInboxItem>,
  ): void {
    setLoadState((prev) => {
      if (prev.kind !== 'loaded') return prev;
      return {
        ...prev,
        items: prev.items.map((it) =>
          it.id === questionId ? { ...it, ...patch } : it,
        ),
      };
    });
  }

  // --- Инструменты автора (CANON G2: вызываются только из onClick QuestionCard). ---

  async function handleHide(questionId: string, hidden: boolean) {
    await apiFetch('/questions/hide', {
      method: 'POST',
      body: { questionId, hidden },
      parse: AuthorActionResponse,
    });
    // Скрытый → 'hidden'. При восстановлении НЕ ставим всегда 'pending' (иначе
    // отвеченный вопрос ложно «становится новым» — баг). Зеркалим серверную
    // логику setQuestionHidden: есть ответ → 'answered', иначе → 'pending'.
    setLoadState((prev) => {
      if (prev.kind !== 'loaded') return prev;
      return {
        ...prev,
        items: prev.items.map((it) => {
          if (it.id !== questionId) return it;
          const restored = it.answer !== null ? 'answered' : 'pending';
          return { ...it, status: hidden ? 'hidden' : restored };
        }),
      };
    });
  }

  async function handleReport(questionId: string, reason: ReportReason) {
    await apiFetch('/questions/report', {
      method: 'POST',
      body: { questionId, reason },
      parse: AuthorActionResponse,
    });
  }

  async function handleBlock(questionId: string, blocked: boolean) {
    await apiFetch('/senders/block', {
      method: 'POST',
      body: { questionId, blocked },
      parse: AuthorActionResponse,
    });
    // Перекладываем карточку в «Заблокированные» (предикат isBlockedItem):
    // блок → status='removed' + moderationFlag='blocked' (как фикстура inbox'а);
    // разблок → возвращаем в обычный список (есть ответ → 'answered', иначе
    // 'pending'), как в handleHide. justAnswered не трогаем.
    setLoadState((prev) => {
      if (prev.kind !== 'loaded') return prev;
      return {
        ...prev,
        items: prev.items.map((it) => {
          if (it.id !== questionId) return it;
          if (blocked) {
            return { ...it, status: 'removed' as const, moderationFlag: 'blocked' as const };
          }
          const restored = it.answer !== null ? 'answered' : 'pending';
          return { ...it, status: restored, moderationFlag: null };
        }),
      };
    });
  }

  if (loadState.kind === 'loading') {
    return (
      <List>
        <InboxSkeleton />
      </List>
    );
  }

  if (loadState.kind === 'error') {
    return (
      <Placeholder header={t.error_title} description={loadState.message} />
    );
  }

  const { box, items, nextCursor } = loadState;

  // Группируем загруженные вопросы по категориям (клиентский фильтр — пробный
  // вид; серверная пагинация по статусам — отдельная задача, если вид приживётся).
  // Пустые категории (0 вопросов) не рисуем — список чище.
  const groups = INBOX_CATEGORIES.map((cat) => ({
    ...cat,
    label: t[cat.labelKey],
    matched: items.filter((i) => cat.match(i, justAnswered)),
    badge: cat.badgeCount(items, justAnswered),
  })).filter((g) => g.matched.length > 0);

  // work-020 (решение 5): онбординг код-входа виден, если открылись без ask-токена,
  // онбординг ещё не закрыт (localStorage + сессия). Взаимоисключение с write-access:
  // пока виден онбординг — write-access-баннер НЕ рисуем (приоритет онбордингу, юзер
  // пришёл задавать вопрос по коду). Закрыл онбординг → следующим всплывёт write-access.
  const showOnboardingBanner = showCodeOnboarding && !codeOnboardingClosed;

  return (
    <List>
      {/* work-036: заголовок сабэкрана «Сообщения» — только для message-под-потока
          (ask-режим — корневая вкладка Tabbar, своего заголовка не имела и не имеет). */}
      {boxFormat === 'message' && <Section header={t.message_inbox_title} />}

      {showOnboardingBanner ? (
        <Banner
          type="inline"
          header={t.code_onboarding_header}
          description={t.code_onboarding_text}
          onCloseIcon={dismissCodeOnboardingBanner}
        >
          <Button mode="bezeled" size="s" onClick={onEnterCode}>
            {t.code_onboarding_action}
          </Button>
        </Banner>
      ) : (
        !writeAccessAsked && (
          <Banner
            type="inline"
            header={t.inbox_notifications_ask}
            onCloseIcon={dismissWriteAccessBanner}
          >
            <Button mode="bezeled" size="s" onClick={handleRequestWriteAccess}>
              {t.inbox_notifications_allow}
            </Button>
          </Banner>
        )
      )}

      {/* Ссылка для приглашений — наверху вкладки (Accordion, открыт по умолчанию). */}
      <InvitePanel
        box={box}
        shareHint={boxFormat === 'message' ? t.message_share_hint : undefined}
      />

      {items.length === 0 ? (
        <Section>
          <div className={styles.emptyHint}>
            <Caption Component="p">
              {boxFormat === 'message' ? t.message_inbox_empty : t.inbox_empty}
            </Caption>
          </div>
        </Section>
      ) : (
        // Категории-аккордеоны друг за другом. «Неотвеченные» раскрыты по
        // умолчанию (см. expanded), у всех — числовой Badge (новые — primary).
        groups.map((g) => {
          const shown = g.matched.slice(0, limits[g.key]);
          // «Ещё» нужен, если локально есть скрытые вопросы категории ИЛИ на
          // сервере остались страницы (mixed-пул) — тогда докачаем.
          const showMore = g.matched.length > limits[g.key] || nextCursor !== null;
          return (
            <Section key={g.key}>
              <Accordion
                expanded={expanded[g.key]}
                onChange={(v) =>
                  setExpanded((p) => ({ ...p, [g.key]: v }))
                }
              >
                <Accordion.Summary className={styles.accHeader}>
                  <span className={styles.accSummary}>
                    <span>{g.label}</span>
                    {g.badge > 0 && (
                      <Badge type="number" mode={g.badgeMode}>
                        {formatBadgeCount(g.badge)}
                      </Badge>
                    )}
                  </span>
                </Accordion.Summary>
                <Accordion.Content>
                  <div className={styles.accInner}>
                    {shown.map((item) => (
                      <QuestionCard
                        key={item.id}
                        item={item}
                        onHide={handleHide}
                        onReport={handleReport}
                        onBlock={handleBlock}
                        onSubmitAnswer={handleSubmitAnswer}
                        onShare={onShareAnswer}
                      />
                    ))}

                    {showMore &&
                      (loadingCat === g.key ? (
                        <InboxBubbleSkeleton />
                      ) : (
                        <div className={styles.loadMoreWrap}>
                          <Button
                            mode="bezeled"
                            size="m"
                            onClick={() =>
                              handleCategoryMore(g.key, g.matched.length)
                            }
                            disabled={loadingMore}
                          >
                            {t.inbox_load_more}
                          </Button>
                          {loadMoreError && (
                            <Caption
                              Component="p"
                              className={styles.loadMoreError}
                            >
                              {loadMoreError}
                            </Caption>
                          )}
                        </div>
                      ))}
                  </div>
                </Accordion.Content>
              </Accordion>
            </Section>
          );
        })
      )}
    </List>
  );
}
